import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import logger, { LogEntry, LogLevel } from '../utils/logger';

interface LoggerContextType {
  logs: LogEntry[];
  config: {
    enabled: boolean;
    logLevel: LogLevel;
    consoleEnabled: boolean;
  };
  clearLogs: () => void;
  setLogLevel: (level: LogLevel) => void;
  setConsoleEnabled: (enabled: boolean) => void;
  setEnabled: (enabled: boolean) => void;
}

const LoggerContext = createContext<LoggerContextType | undefined>(undefined);

export function LoggerProvider({ children }: { children: ReactNode }) {
  const [logs, setLogs] = useState<LogEntry[]>(logger.getLogs());
  const [config, setConfig] = useState(logger.getConfig());

  useEffect(() => {
    // Subscribe to logger updates
    const unsubscribe = logger.subscribe((log) => {
      setLogs(prevLogs => [...prevLogs, log].slice(-1000)); // Keep last 1000 logs
    });

    return unsubscribe;
  }, []);

  const clearLogs = () => {
    logger.clear();
    setLogs([]);
  };

  const setLogLevel = (level: LogLevel) => {
    logger.setLogLevel(level);
    setConfig(logger.getConfig());
  };

  const setConsoleEnabled = (enabled: boolean) => {
    logger.setConsoleEnabled(enabled);
    setConfig(logger.getConfig());
  };

  const setEnabled = (enabled: boolean) => {
    logger.setEnabled(enabled);
    setConfig(logger.getConfig());
  };

  return (
    <LoggerContext.Provider
      value={{
        logs,
        config,
        clearLogs,
        setLogLevel,
        setConsoleEnabled,
        setEnabled,
      }}
    >
      {children}
    </LoggerContext.Provider>
  );
}

export function useLogger() {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }
  return context;
}